import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm, UseFormProps } from 'react-hook-form'
import { z } from 'zod'

import { gsdPerTreatment } from '@/constants/gsd-per-treatment'
import { useDemandAnalysisStore } from '@/pages/demand-analysis/contexts/demand-analysis-store-context'
import { useDemandAnalysisRoute } from '@/pages/demand-analysis/hooks/use-demand-analysis-route'
import {
  mosaicSchema,
  perimeterSchema,
} from '@/pages/demands/steps/select-files/hooks/use-select-files-form'
import {
  validateMosaicFile,
  validatePerimetersFiles,
} from '@/pages/demands/steps/select-files/utils'

const selectFilesSchema = z
  .object({
    mosaic: z
      .discriminatedUnion('modification_flag', [
        mosaicSchema.unwrap().extend({
          modification_flag: z.literal(true),
        }),
        mosaicSchema
          .unwrap()
          .partial()
          .extend({
            modification_flag: z.literal(false),
          }),
      ])
      .optional(),
    perimeter: z
      .discriminatedUnion('modification_flag', [
        perimeterSchema.extend({
          modification_flag: z.literal(true),
        }),
        perimeterSchema.partial().extend({
          modification_flag: z.literal(false),
        }),
      ])
      .optional(),
  })
  .default({
    mosaic: {
      modification_flag: false,
    },
    perimeter: {
      modification_flag: false,
    },
  })

export type SelectFilesSchema = z.infer<typeof selectFilesSchema>

export function useSelectFilesForm(props?: UseFormProps<SelectFilesSchema>) {
  const { mosaic, perimeter } = useDemandAnalysisStore((state) => ({
    mosaic: state.mosaic,
    perimeter: state.perimeter,
  }))

  const methods = useForm<SelectFilesSchema>({
    resolver: zodResolver(selectFilesSchema),
    defaultValues: {
      mosaic: mosaic
        ? {
            input: [mosaic.input],
            results: mosaic?.results,
            modification_flag: true,
          }
        : undefined,
      perimeter: perimeter
        ? {
            input: perimeter?.shapefile,
            results: {
              filename: perimeter?.results.filename,
              area: perimeter?.results.area,
              geojson: perimeter?.results.geojson,
              lat_long: perimeter?.results.centroid,
            },
            modification_flag: true,
          }
        : undefined,
    },
    ...props,
  })

  const { watch, setValue, setError, clearErrors, resetField } = methods

  const { treatment } = useDemandAnalysisRoute()

  const [
    mosaicModificationFlag,
    mosaicInput,
    perimeterModificationFlag,
    perimeterInput,
  ] = watch([
    'mosaic.modification_flag',
    'mosaic.input',
    'perimeter.modification_flag',
    'perimeter.input',
  ])

  useEffect(() => {
    const validateMosaic = async () => {
      if (mosaicModificationFlag) {
        clearErrors('mosaic')
        resetField('mosaic.results', {
          defaultValue: null as any,
        })

        if (!mosaicInput?.length) return

        try {
          const { min, max } = gsdPerTreatment[treatment!]

          const results = await validateMosaicFile(mosaicInput[0], min, max)

          setValue('mosaic.results', results)
        } catch (error: any) {
          setError('mosaic.input', {
            type: 'custom',
            message: error.message,
          })
        }
      }
    }
    validateMosaic()
  }, [mosaicModificationFlag, mosaicInput])

  useEffect(() => {
    const validatePerimeter = async () => {
      if (perimeterModificationFlag) {
        clearErrors('perimeter')
        setValue('perimeter.results', null as any)

        if (!perimeterInput?.length) return

        try {
          const { geojson, bounds, filename, interestArea } =
            await validatePerimetersFiles(perimeterInput)

          setValue('perimeter.results', {
            filename,
            area: interestArea,
            geojson,
            lat_long: [bounds.lat, bounds.lng],
          })
        } catch (error: any) {
          setError('perimeter.input', {
            type: 'custom',
            message: error.message,
          })
        }
      }
    }
    validatePerimeter()
  }, [perimeterModificationFlag, perimeterInput])

  return methods
}
